'use client';

import LandingLayout from '@/components/LandingLayout';
import WebsiteDemo from '@/components/WebsiteDemo';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView } from '@/lib/analytics';
import Link from 'next/link';
import { useEffect } from 'react';

export default function TryPage() {
  const { isAuthenticated } = useAuth();

  // Track page view on mount
  useEffect(() => {
    trackPageView('Try Page', typeof window !== 'undefined' ? window.location.href : undefined, {
      page_name: 'try',
      funnel_step: 'demo_creation'
    });
  }, []);

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-100 via-purple-50 to-green-100 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-purple-400/10"></div>
        <div className="absolute top-10 left-10 w-20 h-20 bg-blue-400/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-400/20 rounded-full blur-xl"></div>

        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-12 lg:py-20 relative">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Create Your AI Phone Agent
              </span>
              <br />
              <span className="text-gray-900">In Seconds</span>
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Enter your website URL and we&apos;ll create a custom AI phone agent that knows your business.
              No signup required.
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <WebsiteDemo
              showBrowserMockup={true}
              showVoiceSelector={true}
              showPromptSection={true}
              showSignupCTA={true}
              analyticsContext="try_page"
              isAuthenticated={isAuthenticated}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Enter Your URL</h3>
              <p className="text-gray-600">
                Just paste your website URL. Our AI reads your business info, services, and pricing.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Creates Your Agent</h3>
              <p className="text-gray-600">
                In about 30 seconds, we generate a custom AI phone agent trained on your business.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Call & Test</h3>
              <p className="text-gray-600">
                Call the demo number with your extension and have a real conversation with your AI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Never Miss a Call Again?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start your free trial today. No credit card required.
          </p>
          <Link
            href="/register"
            onClick={() => trackButtonClick('try_cta_signup', 'try_page')}
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-bold text-xl rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
          >
            Start Free Trial
          </Link>
        </div>
      </section>
    </LandingLayout>
  );
}
